import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/app/Header";
import { BackButton } from "../components/app/BackButton";
import { Button } from "../components/ui/button";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Crown, Sparkles, Loader2, ExternalLink, Infinity as InfinityIcon } from "lucide-react";

export default function Billing() {
  const { user, loading } = useAuth();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [quota, setQuota] = useState(null);
  const [portalLoading, setPortalLoading] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!user) return;
    api.get("/billing/quota").then(({ data }) => setQuota(data)).catch(() => {});
  }, [user]);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const { data } = await api.get(`/stripe/portal?origin=${encodeURIComponent(window.location.origin)}`);
      window.location.href = data.url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Portal error");
      setPortalLoading(false);
    }
  };

  if (loading || !user) return <div className="min-h-screen flex items-center justify-center"><Loader2 className="h-5 w-5 animate-spin text-bull" /></div>;

  const isOwner = user.role === "OWNER";
  const plan = isOwner ? "OWNER" : (quota?.plan || user.plan || "FREE");
  const unlimited = quota?.unlimited;
  const used = quota?.used ?? 0;
  const limit = quota?.limit;
  const pct = limit ? Math.min(100, (used / limit) * 100) : 0;
  const resetAt = quota?.reset_at ? new Date(quota.reset_at) : null;

  return (
    <div className="min-h-screen" data-testid="billing-page">
      <Header />
      <main className="mx-auto max-w-4xl px-6 md:px-8 py-8 space-y-6">
        <BackButton fallback="/dashboard" />
        <div>
          <span className="label-mono">DASHBOARD //</span>
          <h1 className="mt-2 text-3xl sm:text-4xl font-semibold tracking-tighter">{t("billing_title")}</h1>
        </div>

        {/* OWNER view */}
        {isOwner ? (
          <div className="surface-card p-8 flex items-start gap-4" data-testid="owner-billing-card">
            <Crown className="h-8 w-8 text-warn shrink-0" strokeWidth={1.5} />
            <div>
              <span className="label-mono text-warn">{t("billing_owner_title")}</span>
              <p className="mt-2 text-neutral-200">{t("billing_owner_desc")}</p>
            </div>
          </div>
        ) : (
          <>
            {/* Current Plan */}
            <div className="surface-card p-6 space-y-5" data-testid="current-plan-card">
              <div className="flex items-center justify-between">
                <span className="label-mono">{t("billing_current_plan")}</span>
                <span className={`font-mono text-xs px-2.5 py-1 border ${plan === "PRO" ? "border-bull text-bull bg-bull/[0.06]" : plan === "TRADER" ? "border-bull/60 text-bull" : "border-white/20 text-neutral-300"}`}>
                  {t(`plan_${plan}`)}
                </span>
              </div>

              <div>
                <div className="flex items-center justify-between font-mono text-sm mb-2">
                  <span className="text-neutral-400">{t("billing_analyses_used")}</span>
                  <span className="text-white">
                    {unlimited ? <span className="text-bull inline-flex items-center gap-1.5"><InfinityIcon className="h-4 w-4" strokeWidth={1.5} /> {t("quota_unlimited")}</span> : `${used} / ${limit}`}
                  </span>
                </div>
                {!unlimited && (
                  <div className="h-2 bg-white/5 overflow-hidden">
                    <div className={`h-full ${pct >= 100 ? "bg-bear" : pct >= 80 ? "bg-warn" : "bg-bull"}`} style={{ width: `${pct}%` }} />
                  </div>
                )}
                {resetAt && (
                  <p className="mt-2 font-mono text-[11px] text-neutral-500">
                    {t("quota_resets_on")} {resetAt.toLocaleDateString()}
                  </p>
                )}
              </div>

              {plan !== "PRO" && (
                <Button onClick={() => navigate("/pricing")} className="rounded-none bg-bull text-black hover:bg-bull/90 h-11 font-medium" data-testid="upgrade-btn">
                  {t("billing_upgrade")}
                </Button>
              )}
            </div>

            {/* Subscription */}
            <div className="surface-card p-6 space-y-4" data-testid="subscription-card">
              <span className="label-mono">{t("billing_subscription")}</span>
              {user.stripe_subscription_id ? (
                <>
                  <div className="grid grid-cols-2 gap-4 font-mono text-sm">
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">{t("billing_status")}</div>
                      <div className="mt-1 text-bull capitalize">{quota?.subscription_status || user.subscription_status || "—"}</div>
                    </div>
                    <div>
                      <div className="text-[10px] tracking-[0.18em] uppercase text-neutral-500">PLAN</div>
                      <div className="mt-1 text-white">{t(`plan_${plan}`)}</div>
                    </div>
                  </div>
                  <Button onClick={openPortal} disabled={portalLoading} variant="outline" className="rounded-none border-white/15 bg-transparent hover:bg-white/5 h-11" data-testid="manage-portal-btn">
                    {portalLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <>{t("billing_manage")} <ExternalLink className="h-3.5 w-3.5 ml-1.5" strokeWidth={1.5} /></>}
                  </Button>
                </>
              ) : (
                <p className="text-sm text-neutral-400">{t("billing_no_sub")}</p>
              )}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
